import { ErrorBlock } from "@/components/feedback/error/AlertError";
import { SortDropdown } from "@/components/ui/sortDropdown";
import { lead_spesific_key } from "@/config/endpoints";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import { isHiddenKeyField } from "@/features/customField/api/useKeyFields";
import useBranches from "@/features/users/api/useBranches";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";

export type StatusFilter = "active" | "inactive" | "all";
export type WorkloadSort = "none" | "least" | "most";

export interface AssignFilters {
  branchId: string;
  status: StatusFilter;
  workload: WorkloadSort;
  /** Map of customInputId → selected option id (single-select per CF). */
  customFields: Record<string, string>;
}

export const DEFAULT_ASSIGN_FILTERS: AssignFilters = {
  branchId: "",
  status: "active",
  workload: "none",
  customFields: {},
};

const STATUS_ITEMS = [
  { label: "Active users", value: "active" },
  { label: "Inactive users", value: "inactive" },
  { label: "All users", value: "all" },
];

const WORKLOAD_ITEMS = [
  { label: "Default order", value: "none" },
  { label: "Least loaded", value: "least" },
  { label: "Most loaded", value: "most" },
];

const PILL_CLASS =
  "rounded-[0.75rem] bg-white hover:bg-black/10 shadow-none w-auto min-w-[8.5rem]";

interface Props {
  showFilter: boolean;
  filters: AssignFilters;
  onChange: (next: AssignFilters) => void;
}

export default function FilterSection({ showFilter, filters, onChange }: Props) {
  const { spesificFields, isLoading, error, refetch } = useSpesificCustoms({
    queryKey: lead_spesific_key,
    modelName: "leads",
    type: "SELECTOR",
  });
  const { branchSelects, isLoading: branchesLoading } = useBranches();

  const rbacSelectorFields = useMemo(
    () =>
      (spesificFields ?? []).filter(
        (f: any) =>
          f.type === "SELECTOR" &&
          f.isNeedPermission &&
          // A key field ("Team") hidden from this viewer is no filter here.
          !isHiddenKeyField(f) &&
          Array.isArray(f.options) &&
          f.options.length > 0,
      ),
    [spesificFields],
  );

  if (error) {
    return (
      <div className="flex items-center justify-center py-6 w-full">
        <ErrorBlock message={error.message} retry={refetch} />
      </div>
    );
  }

  const showBranchPill = (branchSelects?.length ?? 0) >= 2;

  const setStatus = (label: string) => {
    const item = STATUS_ITEMS.find((i) => i.label === label);
    if (!item) return;
    onChange({ ...filters, status: item.value as StatusFilter });
  };

  const setWorkload = (label: string) => {
    const item = WORKLOAD_ITEMS.find((i) => i.label === label);
    if (!item) return;
    onChange({ ...filters, workload: item.value as WorkloadSort });
  };

  const setBranch = (label: string) => {
    if (label === "All branches") {
      onChange({ ...filters, branchId: "" });
      return;
    }
    const match = (branchSelects ?? []).find(
      (b: { value: string; name: string }) => b.name === label,
    );
    onChange({ ...filters, branchId: match ? match.value : "" });
  };

  const setCustomField = (fieldId: string, options: any[], label: string) => {
    if (label.startsWith("All ")) {
      const next = { ...filters.customFields };
      delete next[fieldId];
      onChange({ ...filters, customFields: next });
      return;
    }
    const opt = options.find((o: any) => String(o.value) === label);
    if (!opt) return;
    onChange({
      ...filters,
      customFields: { ...filters.customFields, [fieldId]: String(opt.id) },
    });
  };

  const branchItems = useMemo(
    () => [
      { label: "All branches", value: "" },
      ...(branchSelects ?? []).map((b: { value: string; name: string }) => ({
        label: b.name,
        value: b.value,
      })),
    ],
    [branchSelects],
  );

  const branchSelectedLabel = useMemo(() => {
    if (!filters.branchId) return "All branches";
    const match = (branchSelects ?? []).find(
      (b: { value: string }) => b.value === filters.branchId,
    );
    return match ? match.name : "All branches";
  }, [filters.branchId, branchSelects]);

  const statusSelectedLabel =
    STATUS_ITEMS.find((i) => i.value === filters.status)?.label ??
    "Active users";

  const workloadSelectedLabel =
    WORKLOAD_ITEMS.find((i) => i.value === filters.workload)?.label ??
    "Default order";

  return (
    <div
      className={`grid transition-all duration-300 ease-in-out ${showFilter ? "grid-rows-[1fr] opacity-100 " : "grid-rows-[0fr] opacity-0 mb-0"}`}
    >
      <div className="overflow-hidden min-h-0">
        <div className="flex gap-2 overflow-x-auto w-full pb-1 no-scrollbar">
          {isLoading || branchesLoading ? (
            <div className="flex items-center justify-center w-full h-full py-2">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <>
              {showBranchPill && (
                <SortDropdown
                  value={branchSelectedLabel}
                  menuItems={branchItems}
                  onSelect={setBranch}
                  isIconShow={false}
                  className={PILL_CLASS}
                />
              )}

              <SortDropdown
                value={statusSelectedLabel}
                menuItems={STATUS_ITEMS}
                onSelect={setStatus}
                isIconShow={false}
                className={PILL_CLASS}
              />

              <SortDropdown
                value={workloadSelectedLabel}
                menuItems={WORKLOAD_ITEMS}
                onSelect={setWorkload}
                isIconShow={false}
                className={PILL_CLASS}
              />

              {rbacSelectorFields.map((field: any) => {
                const fieldId = String(field.id);
                const options = field.options ?? [];
                const lcName = getCfDisplayName(field).toLowerCase();
                const items = [
                  { label: `All ${lcName}`, value: "" },
                  ...options.map((o: any) => ({
                    label: String(o.value),
                    value: String(o.id),
                  })),
                ];
                const selectedId = filters.customFields[fieldId];
                const selectedLabel = selectedId
                  ? (options.find((o: any) => String(o.id) === selectedId)
                      ?.value ?? `All ${lcName}`)
                  : `All ${lcName}`;

                return (
                  <SortDropdown
                    key={fieldId}
                    value={selectedLabel}
                    menuItems={items}
                    onSelect={(label) =>
                      setCustomField(fieldId, options, label)
                    }
                    isIconShow={false}
                    className={PILL_CLASS}
                  />
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Maps the local `AssignFilters` state into query-string params accepted by
 * the `/role-pipeline` endpoint (see roles.repo.ts → buildCustomFieldAccessConditions
 * and the branchId / withLeadCount handling).
 */
export function buildRolePipelineParams(
  filters: AssignFilters,
): Record<string, string> {
  const params: Record<string, string> = { withLeadCount: "true" };
  if (filters.branchId) params.branchId = filters.branchId;
  for (const [fieldId, optionId] of Object.entries(filters.customFields)) {
    if (!optionId) continue;
    params[`cf_${fieldId}`] = optionId;
  }
  return params;
}
